import {
  RELATIONSHIP_LENSES,
  type GraphDataset,
  type GraphPerson,
  type GraphRelationship,
  type RelationshipLens,
  type SearchPerson,
} from '../data/graph-types.js';
import {
  compareRelationshipStartsDescending,
  compareRepresentativeRelationships,
  relationshipConnectionKey,
  relationshipPairKey,
  type EdgePublicationStatus,
  type FeaturedTrace,
  type Source,
} from '@entangled-vega/shared';
import {
  relationshipMatchesFilters,
  type FocusDepth,
  type RelationshipFilter,
} from './relationship-view.js';

export type RelationshipConnection = {
  key: string;
  source: string;
  target: string;
  lens: RelationshipLens;
  phases: readonly GraphRelationship[];
  representative: GraphRelationship;
};

export type CounterpartConnection = {
  personId: string;
  connections: readonly RelationshipConnection[];
};

export type GraphIndex = {
  peopleById: ReadonlyMap<string, GraphPerson>;
  relationshipsById: ReadonlyMap<string, GraphRelationship>;
  relationshipsByPerson: ReadonlyMap<string, readonly GraphRelationship[]>;
  relationshipConnections: readonly RelationshipConnection[];
  connectionsByPerson: ReadonlyMap<string, readonly RelationshipConnection[]>;
  counterpartsByPerson: ReadonlyMap<string, readonly CounterpartConnection[]>;
  connectionByRelationshipId: ReadonlyMap<string, RelationshipConnection>;
  connectionByKey: ReadonlyMap<string, RelationshipConnection>;
  neighborsByPerson: ReadonlyMap<string, readonly string[]>;
  relationshipByPair: ReadonlyMap<string, GraphRelationship>;
  sourcesById: ReadonlyMap<string, Source>;
  hubs: readonly string[];
};

export type GraphPath = {
  people: readonly string[];
  relationships: readonly GraphRelationship[];
  totalCost: number;
};

export function pairKey(left: string, right: string): string {
  return relationshipPairKey(left, right);
}

export function otherEndpoint(relationship: GraphRelationship, personId: string): string {
  return relationship.source === personId ? relationship.target : relationship.source;
}

export function neighborhoodPeople(
  index: GraphIndex,
  personId: string,
  maximumHops: 1 | 2,
): ReadonlyMap<string, 0 | 1 | 2> {
  if (!index.peopleById.has(personId)) {
    return new Map();
  }
  const distances = new Map<string, 0 | 1 | 2>([[personId, 0]]);
  let frontier = [personId];
  for (let hop = 1; hop <= maximumHops; hop += 1) {
    const next: string[] = [];
    for (const current of frontier) {
      for (const neighbor of index.neighborsByPerson.get(current) ?? []) {
        if (distances.has(neighbor)) {
          continue;
        }
        distances.set(neighbor, hop as 1 | 2);
        next.push(neighbor);
      }
    }
    frontier = next;
  }
  return distances;
}

export function focusedPeopleForPerson(options: {
  index: GraphIndex;
  personId: string;
  depth: FocusDepth;
  relationshipFilters: readonly RelationshipFilter[];
}): ReadonlyMap<string, 0 | 1 | 2> {
  if (!options.index.peopleById.has(options.personId)) {
    return new Map();
  }
  const distances = new Map<string, 0 | 1 | 2>([[options.personId, 0]]);
  const directNeighbors: string[] = [];
  const allDirectNeighbors = new Set<string>();
  for (const connection of options.index.connectionsByPerson.get(options.personId) ?? []) {
    const neighbor = connection.source === options.personId ? connection.target : connection.source;
    allDirectNeighbors.add(neighbor);
    if (
      !connection.phases.some((phase) =>
        relationshipMatchesFilters(phase, options.personId, options.relationshipFilters),
      )
    ) {
      continue;
    }
    if (!distances.has(neighbor)) {
      distances.set(neighbor, 1);
      directNeighbors.push(neighbor);
    }
  }
  if (options.depth !== 'two-hop') {
    return distances;
  }
  for (const directNeighbor of directNeighbors) {
    for (const neighbor of options.index.neighborsByPerson.get(directNeighbor) ?? []) {
      if (!distances.has(neighbor) && !allDirectNeighbors.has(neighbor)) {
        distances.set(neighbor, 2);
      }
    }
  }
  return distances;
}

/**
 * Keep the complete public-figure catalog available in the HTML fallback while
 * revealing subordinate context people only when the current exploration makes
 * them relevant. This mirrors the constellation's overview, selected-person
 * depth, filter, and trace contract without maintaining a second graph.
 */
export function peopleForListMode(options: {
  dataset: GraphDataset;
  index: GraphIndex;
  selectedId: string | null;
  path: GraphPath | null;
  focusDepth: FocusDepth;
  relationshipFilters: readonly RelationshipFilter[];
}): GraphPerson[] {
  const relevantContextIds = new Set(options.path?.people ?? []);
  if (options.selectedId !== null) {
    for (const personId of focusedPeopleForPerson({
      index: options.index,
      personId: options.selectedId,
      depth: options.focusDepth,
      relationshipFilters: options.relationshipFilters,
    }).keys()) {
      relevantContextIds.add(personId);
    }
  }
  return options.dataset.people.filter(
    (person) => person.inclusionRole !== 'context' || relevantContextIds.has(person.id),
  );
}

export function graphDatasetForLenses(
  dataset: GraphDataset,
  enabledLenses: readonly RelationshipLens[],
): GraphDataset {
  const enabled = new Set(enabledLenses);
  const relationships = dataset.relationships.filter((relationship) =>
    enabled.has(relationship.lens),
  );
  if (relationships.length === dataset.relationships.length) {
    return dataset;
  }
  return {
    ...dataset,
    relationships,
    indexes: undefined,
  };
}

export function createGraphIndex(dataset: GraphDataset): GraphIndex {
  const peopleById = new Map(dataset.people.map((person) => [person.id, person]));
  const relationshipsById = new Map(
    dataset.relationships.map((relationship) => [relationship.id, relationship]),
  );
  const relationshipsByPerson = new Map(
    dataset.people.map((person) => [person.id, [] as GraphRelationship[]]),
  );
  const neighborSets = new Map(dataset.people.map((person) => [person.id, new Set<string>()]));
  const relationshipByPair = new Map<string, GraphRelationship>();

  if (dataset.indexes === undefined) {
    for (const relationship of dataset.relationships) {
      if (!peopleById.has(relationship.source) || !peopleById.has(relationship.target)) {
        continue;
      }
      relationshipsByPerson.get(relationship.source)?.push(relationship);
      relationshipsByPerson.get(relationship.target)?.push(relationship);
      neighborSets.get(relationship.source)?.add(relationship.target);
      neighborSets.get(relationship.target)?.add(relationship.source);
    }
  } else {
    for (const [personIndex, edgeIndexes] of dataset.indexes.edgeIndexesByPerson.entries()) {
      const person = dataset.people[personIndex];
      if (person === undefined) {
        continue;
      }
      for (const edgeIndex of edgeIndexes) {
        const relationship = dataset.relationships[edgeIndex];
        if (relationship === undefined) {
          continue;
        }
        relationshipsByPerson.get(person.id)?.push(relationship);
        const neighbor = otherEndpoint(relationship, person.id);
        if (peopleById.has(neighbor)) {
          neighborSets.get(person.id)?.add(neighbor);
        }
      }
    }
  }

  for (const relationship of dataset.relationships) {
    const key = pairKey(relationship.source, relationship.target);
    const existing = relationshipByPair.get(key);
    if (existing === undefined || compareRepresentativeRelationships(relationship, existing) < 0) {
      relationshipByPair.set(key, relationship);
    }
  }

  for (const [personId, relationships] of relationshipsByPerson) {
    relationships.sort((left, right) => {
      const leftName = peopleById.get(otherEndpoint(left, personId))?.name ?? '';
      const rightName = peopleById.get(otherEndpoint(right, personId))?.name ?? '';
      return leftName.localeCompare(rightName) || left.id.localeCompare(right.id);
    });
  }

  const neighborsByPerson = new Map(
    [...neighborSets].map(([personId, neighbors]) => [
      personId,
      [...neighbors].sort((left, right) => {
        const leftName = peopleById.get(left)?.name ?? left;
        const rightName = peopleById.get(right)?.name ?? right;
        return leftName.localeCompare(rightName) || left.localeCompare(right);
      }),
    ]),
  );
  const phaseGroups = new Map<string, GraphRelationship[]>();
  for (const relationship of dataset.relationships) {
    if (!peopleById.has(relationship.source) || !peopleById.has(relationship.target)) {
      continue;
    }
    const key = relationshipConnectionKey(
      relationship.source,
      relationship.target,
      relationship.lens,
    );
    const phases = phaseGroups.get(key) ?? [];
    phases.push(relationship);
    phaseGroups.set(key, phases);
  }
  const relationshipConnections = [...phaseGroups]
    .sort(([left], [right]) => left.localeCompare(right))
    .map<RelationshipConnection>(([key, unsortedPhases]) => {
      const phases = [...unsortedPhases].sort((left, right) => {
        const dateOrder = compareRelationshipStartsDescending(left, right);
        return (
          dateOrder ||
          left.relation.localeCompare(right.relation) ||
          left.label.localeCompare(right.label) ||
          left.id.localeCompare(right.id)
        );
      });
      const representative = [...phases].sort(compareRepresentativeRelationships)[0];
      if (representative === undefined) {
        throw new Error(`Relationship connection ${key} has no phases.`);
      }
      const [source, target] = pairKey(representative.source, representative.target).split('|');
      if (source === undefined || target === undefined) {
        throw new Error(`Relationship connection ${key} has invalid endpoints.`);
      }
      return { key, source, target, lens: representative.lens, phases, representative };
    });
  const connectionsByPerson = new Map(
    dataset.people.map((person) => [person.id, [] as RelationshipConnection[]]),
  );
  const connectionByRelationshipId = new Map<string, RelationshipConnection>();
  for (const connection of relationshipConnections) {
    connectionsByPerson.get(connection.source)?.push(connection);
    connectionsByPerson.get(connection.target)?.push(connection);
    for (const phase of connection.phases) {
      connectionByRelationshipId.set(phase.id, connection);
    }
  }
  for (const [personId, connections] of connectionsByPerson) {
    connections.sort((left, right) => {
      const leftName =
        peopleById.get(left.source === personId ? left.target : left.source)?.name ?? '';
      const rightName =
        peopleById.get(right.source === personId ? right.target : right.source)?.name ?? '';
      return (
        leftName.localeCompare(rightName) ||
        RELATIONSHIP_LENSES.indexOf(left.lens) - RELATIONSHIP_LENSES.indexOf(right.lens) ||
        left.key.localeCompare(right.key)
      );
    });
  }
  const counterpartsByPerson = new Map<string, CounterpartConnection[]>();
  for (const person of dataset.people) {
    const grouped = new Map<string, RelationshipConnection[]>();
    for (const connection of connectionsByPerson.get(person.id) ?? []) {
      const otherId = connection.source === person.id ? connection.target : connection.source;
      const connections = grouped.get(otherId) ?? [];
      connections.push(connection);
      grouped.set(otherId, connections);
    }
    counterpartsByPerson.set(
      person.id,
      [...grouped]
        .map(([personId, connections]) => ({ personId, connections }))
        .sort((left, right) => {
          const leftName = peopleById.get(left.personId)?.name ?? left.personId;
          const rightName = peopleById.get(right.personId)?.name ?? right.personId;
          return leftName.localeCompare(rightName) || left.personId.localeCompare(right.personId);
        }),
    );
  }
  const hubs =
    dataset.indexes === undefined
      ? [...dataset.people]
          .sort((left, right) => {
            const degreeDifference =
              (neighborsByPerson.get(right.id)?.length ?? 0) -
              (neighborsByPerson.get(left.id)?.length ?? 0);
            return degreeDifference || left.name.localeCompare(right.name);
          })
          .slice(0, 8)
          .map((person) => person.id)
      : dataset.indexes.hubPersonIndexes
          .map((personIndex) => dataset.people[personIndex]?.id)
          .filter((personId): personId is string => personId !== undefined)
          .slice(0, 8);

  return {
    peopleById,
    relationshipsById,
    relationshipsByPerson,
    relationshipConnections,
    connectionsByPerson,
    counterpartsByPerson,
    connectionByRelationshipId,
    connectionByKey: new Map(
      relationshipConnections.map((connection) => [connection.key, connection]),
    ),
    neighborsByPerson,
    relationshipByPair,
    sourcesById: new Map((dataset.sources ?? []).map((source) => [source.id, source])),
    hubs,
  };
}

type QueueEntry = {
  personId: string;
  cost: number;
  signature: string;
};

const PROVENANCE_COST: Readonly<Record<EdgePublicationStatus, number>> = {
  documented: 1,
  wikidata: 1,
  reported: 3,
  disputed: 4,
  rumored: 5,
};

function traversalCost(
  index: GraphIndex,
  relationship: GraphRelationship,
  nextPersonId: string,
): number {
  const nextPerson = index.peopleById.get(nextPersonId);
  const degree = index.neighborsByPerson.get(nextPersonId)?.length ?? 0;
  const provenanceCost = PROVENANCE_COST[relationship.status];
  const familyCost = relationship.kind === 'family' ? 0.04 : 0;
  const hubCost = Math.log2(degree + 1) * 0.04;
  const recognition = nextPerson?.recognitionScore ?? 0;
  const recognitionCost = recognition <= 0 ? 0.08 : 0.05 / Math.log2(recognition + 2);
  return provenanceCost + familyCost + hubCost + recognitionCost;
}

export function featuredTracePath(index: GraphIndex, trace: FeaturedTrace): GraphPath | null {
  if (!index.peopleById.has(trace.source) || !index.peopleById.has(trace.target)) {
    return null;
  }
  const people = [trace.source];
  const relationships: GraphRelationship[] = [];
  let current = trace.source;
  let totalCost = 0;
  for (const relationshipId of trace.edgeIds) {
    const relationship = index.relationshipsById.get(relationshipId);
    if (
      relationship === undefined ||
      (relationship.source !== current && relationship.target !== current)
    ) {
      return null;
    }
    const next = otherEndpoint(relationship, current);
    if (!index.peopleById.has(next)) {
      return null;
    }
    relationships.push(relationship);
    people.push(next);
    totalCost += traversalCost(index, relationship, next);
    current = next;
  }
  return current === trace.target ? { people, relationships, totalCost } : null;
}

export function bestDocumentedPath(
  index: GraphIndex,
  source: string,
  target: string,
): GraphPath | null {
  if (!index.peopleById.has(source) || !index.peopleById.has(target)) {
    return null;
  }
  if (source === target) {
    return { people: [source], relationships: [], totalCost: 0 };
  }

  const distances = new Map<string, { cost: number; signature: string }>([
    [source, { cost: 0, signature: source }],
  ]);
  const previous = new Map<string, { personId: string; relationship: GraphRelationship }>();
  const queue: QueueEntry[] = [{ personId: source, cost: 0, signature: source }];
  while (queue.length > 0) {
    queue.sort(
      (left, right) =>
        left.cost - right.cost ||
        left.signature.localeCompare(right.signature) ||
        left.personId.localeCompare(right.personId),
    );
    const current = queue.shift();
    if (current === undefined) {
      continue;
    }
    const known = distances.get(current.personId);
    if (
      known === undefined ||
      current.cost > known.cost + Number.EPSILON ||
      (Math.abs(current.cost - known.cost) <= Number.EPSILON &&
        current.signature !== known.signature)
    ) {
      continue;
    }
    if (current.personId === target) {
      break;
    }
    for (const connection of index.connectionsByPerson.get(current.personId) ?? []) {
      const relationship = connection.representative;
      const neighbor = otherEndpoint(relationship, current.personId);
      const nextCost = current.cost + traversalCost(index, relationship, neighbor);
      const signature = `${current.signature}|${relationship.id}|${neighbor}`;
      const prior = distances.get(neighbor);
      if (
        prior !== undefined &&
        (nextCost > prior.cost + Number.EPSILON ||
          (Math.abs(nextCost - prior.cost) <= Number.EPSILON &&
            signature.localeCompare(prior.signature) >= 0))
      ) {
        continue;
      }
      distances.set(neighbor, { cost: nextCost, signature });
      previous.set(neighbor, { personId: current.personId, relationship });
      queue.push({ personId: neighbor, cost: nextCost, signature });
    }
  }
  const result = distances.get(target);
  if (result === undefined) {
    return null;
  }
  const people = [target];
  const relationships: GraphRelationship[] = [];
  let current = target;
  while (current !== source) {
    const step = previous.get(current);
    if (step === undefined) {
      throw new Error('Path predecessor chain ended before the source endpoint.');
    }
    people.push(step.personId);
    relationships.push(step.relationship);
    current = step.personId;
  }
  people.reverse();
  relationships.reverse();
  return { people, relationships, totalCost: result.cost };
}

export const shortestPath = bestDocumentedPath;

function normalizedSearch(value: string): string {
  return value
    .normalize('NFKD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('en-US')
    .replaceAll(/\s+/g, ' ')
    .trim();
}

export function searchPeople<Person extends SearchPerson>(
  people: readonly Person[],
  query: string,
  maximum = 8,
): Person[] {
  const normalizedQuery = normalizedSearch(query);
  if (normalizedQuery.length === 0) {
    return [];
  }
  return people
    .map((person) => {
      const canonicalName = normalizedSearch(person.name);
      const supplementalKeys = person.searchKeys ?? (person.aliases ?? []).map(normalizedSearch);
      const keys = [canonicalName, ...supplementalKeys.filter((key) => key !== canonicalName)];
      const matchScore = Math.min(
        ...keys.map((key, keyIndex) => {
          const aliasPenalty = keyIndex === 0 ? 0 : 0.25;
          const wordBoundary = key.split(/\s+/u).some((word) => word.startsWith(normalizedQuery));
          return key === normalizedQuery
            ? aliasPenalty
            : key.startsWith(normalizedQuery)
              ? 1 + aliasPenalty
              : wordBoundary
                ? 2 + aliasPenalty
                : key.includes(normalizedQuery)
                  ? 3 + aliasPenalty
                  : 4;
        }),
      );
      const isContext = person.inclusionRole === 'context';
      const strongContextMatch =
        matchScore < 2 ||
        (normalizedQuery.length >= 4 &&
          keys.some((key) => key.split(/\s+/u).some((word) => word.startsWith(normalizedQuery))));
      return {
        person,
        score: matchScore + (isContext ? 2.5 : 0),
        visible: matchScore < 4 && (!isContext || strongContextMatch),
      };
    })
    .filter((entry) => entry.visible)
    .sort(
      (left, right) =>
        left.score - right.score ||
        (right.person.recognitionScore ?? 0) - (left.person.recognitionScore ?? 0) ||
        left.person.name.localeCompare(right.person.name),
    )
    .slice(0, maximum)
    .map((entry) => entry.person);
}
